import * as d3 from "d3";
import { useRef, useEffect } from "react";

const HistoChart = ({ 
  data, 
  dimensions = [500, 300], 
  xLabel = "Value", 
  yLabel = "Count" 
}) => {
  const graphContainerRef = useRef(null);
  const graphNodeRef = useRef(null);

  const createGraph = () => {
    // Clear previous graph if any
    if (graphNodeRef.current && graphContainerRef.current.contains(graphNodeRef.current)) {
      graphContainerRef.current.removeChild(graphNodeRef.current);
    }

    const [width, height] = dimensions;
    const margin = {top: 20, right: 30, bottom: 50, left: 60};
    const w = width - margin.left - margin.right;
    const h = height - margin.top - margin.bottom;

    // Create SVG
    const svg = d3.create("svg")
      .attr("width", width)
      .attr("height", height);

    const g = svg.append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);

    // X axis
    const x = d3.scaleLinear()
      .domain([d3.min(data), d3.max(data) + 1]) // Extend domain by 1 to show final bar
      .nice()
      .range([0, w]);

    g.append("g")
      .attr("transform", `translate(0,${h})`)
      .call(d3.axisBottom(x)
        .ticks(Math.min(30, d3.max(data) - d3.min(data) + 1))
        .tickFormat(d3.format("d")));

    // X axis label
    svg.append("text")
      .attr("text-anchor", "middle")
      .attr("x", margin.left + w / 2)
      .attr("y", height - 10)
      .attr("font-size", "14px")
      .text(xLabel);

    // Create histogram bins
    const bins = d3.bin()
      .domain(x.domain())
      .thresholds(x.ticks(30))(data);

    // Y axis (scale to max bin count)
    const y = d3.scaleLinear()
      .domain([0, d3.max(bins, d => d.length)])
      .range([h, 0]);

    g.append("g")
      .call(d3.axisLeft(y)
        .ticks(Math.min(10, d3.max(bins, d => d.length)))
        .tickFormat(d3.format("d")));

    // Y axis label
    svg.append("text")
      .attr("text-anchor", "middle")
      .attr("transform", `rotate(-90)`)
      .attr("x", -margin.top - h / 2)
      .attr("y", 18)
      .attr("font-size", "14px")
      .text(yLabel);

    // Create bars
    g.selectAll(".bar")
      .data(bins)
      .enter().append("rect")
      .attr("class", "bar")
      .attr("x", d => x(d.x0))
      .attr("width", d => Math.max(0, x(d.x1) - x(d.x0) - 1)) // -1 for gap between bars
      .attr("y", d => y(d.length))
      .attr("height", d => h - y(d.length))
      .attr("fill", "#69b3a2")
      .attr("opacity", 0.7)
      .attr("stroke", "#458B74")
      .attr("stroke-width", 1);

    graphNodeRef.current = svg.node();
    graphContainerRef.current.appendChild(graphNodeRef.current);
  };

  useEffect(() => {
    if (data && data.length > 0) {
      createGraph();
    }
  }, [data, dimensions, xLabel, yLabel]);

  return (
    <div 
      ref={graphContainerRef} 
      style={{
        width: '100%',
        minHeight: '200px'
      }}
    />
  );
};

export default HistoChart;